import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CreditCard, Loader2, Receipt, TrendingUp, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { db, money } from "@/lib/billing";

async function fetchBillingDashboard() {
  const [{ data: subscriptions, error: subError }, { data: invoices, error: invError }, { data: events, error: eventsError }] = await Promise.all([
    db.from("user_subscriptions").select("status, package_id, packages(name, monthly_price, currency)").order("created_at", { ascending: false }),
    db.from("invoices").select("status, amount_paid, amount_due, currency, created_at").order("created_at", { ascending: false }).limit(100),
    db.from("billing_events").select("*").order("created_at", { ascending: false }).limit(10),
  ]);
  if (subError) throw subError;
  if (invError) throw invError;
  if (eventsError) throw eventsError;
  return { subscriptions: subscriptions ?? [], invoices: invoices ?? [], events: events ?? [] };
}

export default function AdminBillingPage() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-billing-dashboard"], queryFn: fetchBillingDashboard });

  const stats = useMemo(() => {
    const subs = data?.subscriptions ?? [];
    const invoices = data?.invoices ?? [];
    const active = subs.filter((s: any) => s.status === "active").length;
    const trials = subs.filter((s: any) => s.status === "trialing").length;
    const failed = subs.filter((s: any) => ["past_due", "unpaid", "incomplete"].includes(s.status)).length;
    const mrr = subs.filter((s: any) => s.status === "active").reduce((sum: number, s: any) => sum + Number(s.packages?.monthly_price ?? 0), 0);
    const paidRevenue = invoices.filter((i: any) => i.status === "paid").reduce((sum: number, i: any) => sum + Number(i.amount_paid ?? 0), 0);
    const distribution = subs.reduce((acc: Record<string, number>, s: any) => {
      const name = s.packages?.name ?? "Unknown";
      acc[name] = (acc[name] ?? 0) + 1;
      return acc;
    }, {});
    return { active, trials, failed, mrr, paidRevenue, distribution };
  }, [data]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Billing Dashboard</h1>
          <p className="text-xs text-muted-foreground">Revenue, subscribers, trials, failed payments, and recent billing events.</p>
        </div>
      </header>
      <main className="space-y-6 p-6">
        {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : (
          <>
            <div className="grid gap-4 md:grid-cols-5">
              <Stat icon={TrendingUp} label="MRR" value={money(stats.mrr)} />
              <Stat icon={Users} label="Active subscribers" value={stats.active} />
              <Stat icon={Users} label="Trial users" value={stats.trials} />
              <Stat icon={AlertTriangle} label="Payment issues" value={stats.failed} />
              <Stat icon={Receipt} label="Paid invoice revenue" value={money(stats.paidRevenue, "usd", true)} />
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle className="text-base">Package distribution</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(stats.distribution).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between rounded-md border border-border p-3">
                      <span className="font-medium">{name}</span>
                      <span className="text-sm text-muted-foreground">{count as number} subscriber(s)</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Recent billing events</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {(data?.events ?? []).map((event: any) => (
                    <div key={event.id} className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                      <div>
                        <div className="font-medium">{event.message}</div>
                        <div className="text-xs text-muted-foreground">{event.event_type}</div>
                      </div>
                      <StatusBadge status={event.severity} />
                    </div>
                  ))}
                  {(data?.events ?? []).length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No billing events yet.</p>}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent"><Icon className="h-5 w-5 text-muted-foreground" /></div>
        <div><p className="text-xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>
      </CardContent>
    </Card>
  );
}
